import { ArrowUp, ExternalLink, Phone } from "lucide-react";
import { useSmoothScroll } from "@/lib/smooth-scroll";
import logo from "@/assets/inkwell-logo.png";

const EXPLORE = [
  { label: "The Work", href: "#work" },
  { label: "About April", href: "#about" },
  { label: "At Last", href: "#at-last" },
  { label: "Insurance & Questions", href: "#insurance" },
  { label: "The Inkwell Papers", href: "#papers" },
  { label: "The Inkwell Journal", href: "#journal" },
  { label: "Questions", href: "#contact" },
];

export function Footer() {
  const { scrollTo } = useSmoothScroll();
  const go = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    scrollTo(href);
  };

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-charcoal">
      <div className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:px-12">
        <div
          role="note"
          data-testid="crisis-notice"
          className="flex flex-col gap-4 border border-gold/30 bg-gold/[0.05] p-6 sm:flex-row sm:items-center sm:gap-6"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold">
            <Phone className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className="text-sm leading-relaxed text-stone">
            <strong className="font-medium text-ivory">
              If you are in immediate danger, call 911.
            </strong>{" "}
            For crisis support, call or text{" "}
            <a href="tel:988" data-testid="crisis-call-link" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-ivory">
              988
            </a>{" "}
            (Suicide &amp; Crisis Lifeline, US, 24/7), or chat via{" "}
            <a
              href="https://988lifeline.org"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="crisis-site-link"
              className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-ivory"
            >
              988lifeline.org
            </a>
            .
          </p>
        </div>

        <div className="mt-14 grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <img
              src={logo}
              alt="The Inkwell — A Marriage and Family Therapy Practice"
              className="w-64 max-w-full"
            />
            <p className="mt-5 text-sm text-stone/60">
              A Marriage &amp; Family Therapy Practice · LMFT #163626
              <br />
              Telehealth throughout California
            </p>
          </div>
          <nav aria-label="Footer" className="md:col-span-4">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-stone/60">Explore</p>
            <ul className="mt-4 space-y-2.5">
              {EXPLORE.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={go(l.href)}
                    data-testid={`footer-link-${l.href.slice(1)}`}
                    className="text-sm text-stone/80 transition-colors hover:text-haint"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <div className="md:col-span-3">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-stone/60">Contact</p>
            <div className="mt-4 space-y-2 text-sm">
              <p>
                <a href="mailto:april@theinkwelltherapy.com" data-testid="footer-email-link" className="text-stone transition-colors hover:text-gold">
                  april@theinkwelltherapy.com
                </a>
              </p>
              <p>
                <a href="tel:+15107300296" data-testid="footer-phone-link" className="text-stone transition-colors hover:text-gold">
                  510.730.0296
                </a>
              </p>
            </div>
            <div className="mt-6 border-l border-gold/50 pl-4">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold">Open Doors</p>
              <p className="mt-1 text-xs text-stone/60">Community resources from The Inkwell</p>
              <a
                href="https://inkwell-open-doors.lovable.app/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-open-doors-link"
                className="mt-2 inline-flex items-center gap-2 text-sm text-stone transition-colors hover:text-gold"
              >
                Enter Open Doors
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-between gap-6 border-t border-white/10 pt-8">
          <p className="text-xs text-stone/50">
            © 2026 The Inkwell. A Marriage &amp; Family Therapy Practice. LMFT #163626
          </p>
          <p className="mt-2 text-xs text-stone/40">
            The woman in these photographs is Maya — a character in The Inkwell’s story.
          </p>
          <button
            type="button"
            data-testid="scroll-to-top-button"
            aria-label="Scroll back to top"
            onClick={() => scrollTo(0)}
            className="group flex items-center gap-3 font-mono text-[11px] tracking-[0.25em] uppercase text-stone/60 transition-colors hover:text-gold"
          >
            Back to top
            <ArrowUp className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1" aria-hidden="true" />
          </button>
        </div>
      </div>
    </footer>
  );
}
